import { Container, Graphics, Text } from 'pixi.js';
import { walls, hoardedFloor } from './art.js';
import { label } from './primitives.js';
import { projectWorld } from './camera.js';

type Point = { x: number; y: number };
type Rect = Point & { w: number; h: number };
export type OpenedArea = { ring: readonly number[]; gate: string; door: Point };

/** Frames between neighbouring panels starting to fall, the fall itself, and the whole show. */
export const STAGGER = 2, FALL = 12, CLEAR = 36, SIGN = 90;

/** How far through its fall a panel `delay` frames down the line is (0 standing, 1 flat), eased in like gravity. */
export function fallProgress(age: number, delay: number): number {
    const t = Math.max(0, Math.min(1, (age - delay) / FALL));
    return t * t;
}

/** Ring centre in world units. */
export function ringCentre(ring: readonly number[]): Point {
    const xs = ring.filter((_, i) => i % 2 === 0), ys = ring.filter((_, i) => i % 2 === 1);
    return { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
}

type Panel = { node: Graphics; dust: Graphics; delay: number; lean: number; base: Point };

/**
 * Presentation only, never read by the sim: hoarding that came down topples outward panel by panel from the
 * opening gate's door with a dust puff each, the striped floor slides away, and a sign says which gate opened it.
 */
export class ExpansionOpening {
    private panels: Panel[] = [];
    private floors: Container[] = [];
    private signs: { text: Text; at: Point }[] = [];
    private at = -1;
    constructor(private world: Container, private floorLayer: Container) {}
    get running() { return this.at >= 0; }

    start(frame: number, removed: readonly Rect[], opened: readonly OpenedArea[], door: Point, reducedMotion: boolean) {
        this.finish();
        this.at = frame;
        const centres = opened.map(x => ringCentre(x.ring));
        for (const area of opened) {
            const c = ringCentre(area.ring), text = label(new Container(), `Gate ${area.gate} open!`, 0, 0, 26, 0xf2b84b, '900');
            text.anchor.set(0.5);
            Object.assign(text.style, { stroke: 0xffffff, strokeThickness: 5, lineJoin: 'round' });
            text.parent!.zIndex = 100000;
            this.world.addChild(text.parent!);
            this.signs.push({ text, at: c });
        }
        if (reducedMotion) { this.step(frame); return; }
        if (opened.length) {
            const floor = hoardedFloor(opened);
            floor.children.slice(2).forEach(child => child.destroy());
            this.floorLayer.addChild(floor);
            this.floors.push(floor);
        }
        // Topple away from the nearest opened area (into the corridor or out onto the apron); outward from the door otherwise.
        const toward = (p: Point) => centres.reduce((best, c) => Math.hypot(c.x - p.x, c.y - p.y) < Math.hypot(best.x - p.x, best.y - p.y) ? c : best, centres[0] ?? door);
        const pieces = removed.flatMap(r => walls([], [], [], [r]).map(node => ({ node: node as Graphics, r })));
        const order = pieces.map(({ node, r }) => {
            const b = node.getLocalBounds(), base = { x: b.x + b.width / 2, y: b.y + b.height };
            return { node, r, base, reach: Math.hypot(r.x - door.x, r.y - door.y) };
        }).sort((a, b) => a.reach - b.reach);
        order.forEach(({ node, r, base }, n) => {
            node.pivot.set(base.x, base.y);
            node.position.set(base.x, base.y);
            const away = projectWorld(toward(r).x, toward(r).y).x;
            const dust = new Graphics();
            dust.position.set(base.x, base.y);
            dust.zIndex = node.zIndex + 1;
            this.world.addChild(node, dust);
            this.panels.push({ node, dust, delay: Math.min(n * STAGGER, 30), lean: away > base.x ? -1 : 1, base });
        });
        this.step(frame);
    }

    /** Advances the show to `frame`; returns false once everything is cleared away. */
    step(frame: number): boolean {
        if (this.at < 0) return false;
        const age = frame - this.at;
        for (const p of this.panels) {
            const t = fallProgress(age, p.delay), landed = age - p.delay - FALL;
            p.node.scale.y = 1 - t * 0.85;
            p.node.skew.x = p.lean * t * 1.1;
            p.node.alpha = landed > 0 ? Math.max(0, 1 - landed / 10) : 1;
            p.dust.clear();
            if (landed >= 0 && landed < 14) {
                const q = landed / 14;
                p.dust.lineStyle(3, 0xd9c9a8, 0.6 * (1 - q)).drawEllipse(0, 0, 14 + landed * 3.2, 7 + landed * 1.6);
                p.dust.beginFill(0xe8dcc0, 0.35 * (1 - q)).drawEllipse(p.lean * -landed * 1.5, -6 - landed, 10 + landed, 6 + landed * 0.5).endFill();
            }
        }
        for (const floor of this.floors) {
            const q = Math.min(1, age / CLEAR);
            floor.children[0].position.set(q * q * 60, -q * q * 60);
            floor.alpha = 1 - q;
        }
        for (const s of this.signs) {
            const o = projectWorld(s.at.x, s.at.y), pop = age < 8 ? 0.6 + age / 8 * 0.55 : age < 14 ? 1.15 - (age - 8) / 6 * 0.15 : 1;
            s.text.parent!.position.set(o.x, o.y - 60 - age * 0.4);
            s.text.scale.set(pop);
            s.text.alpha = Math.min(1, (SIGN - age) / 20);
        }
        if (age >= SIGN) { this.finish(); return false; }
        return true;
    }

    finish() {
        this.panels.forEach(p => { p.node.destroy(); p.dust.destroy(); });
        this.floors.forEach(f => f.destroy({ children: true }));
        this.signs.forEach(s => s.text.parent!.destroy({ children: true }));
        this.panels = []; this.floors = []; this.signs = []; this.at = -1;
    }
}
